/**
 * Sync Engine for Google Drive
 *
 * Handles synchronization of notes, events, and spaces between IndexedDB and Google Drive.
 * Architecture:
 * - IndexedDB = local cache (offline-first)
 * - Google Drive = source of truth (cloud storage)
 * - Conflict resolution: newer timestamp wins
 */

import { driveClient } from './google-drive';
import { calendarClient } from './google-calendar';
import { notesDB, eventsDB, contentItemsDB, syncMetadataDB, deletedItemsDB, type Note, type CalendarEvent, type ContentItem } from './db';
import { extractPlainText } from './note-utils';
import type { JSONContent } from '@tiptap/core';

interface SyncResult {
  success: boolean;
  uploaded: number;
  downloaded: number;
  errors: string[];
}

interface NoteSyncData {
  note: Note;
  version: number; // For future use with Yjs
}

interface EventSyncData {
  event: CalendarEvent;
  version: number; // For future use with Yjs
}

interface ContentItemSyncData {
  item: ContentItem;
  version: number; // For future use with Yjs
}

/**
 * Sync Engine Class
 */
export class SyncEngine {
  private spaceId: string;
  private notesFolderId: string | null = null;
  private eventsFolderId: string | null = null;
  private contentItemsFolderId: string | null = null;

  constructor(spaceId: string = 'default') {
    this.spaceId = spaceId;
  }

  /**
   * Initialize folder structure for the space
   */
  async initialize(): Promise<void> {
    try {
      const { notesFolder, calendarFolder, contentItemsFolder } = await driveClient.getOrCreateSpaceFolder(this.spaceId);
      this.notesFolderId = notesFolder.id;
      this.eventsFolderId = calendarFolder.id;
      this.contentItemsFolderId = contentItemsFolder.id;

      // Store folder IDs in sync metadata
      await syncMetadataDB.set(`folder_${this.spaceId}_notes`, notesFolder.id);
      await syncMetadataDB.set(`folder_${this.spaceId}_events`, calendarFolder.id);
      await syncMetadataDB.set(`folder_${this.spaceId}_contentItems`, contentItemsFolder.id);
    } catch (error) {
      console.error('[SyncEngine] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Ensure we have the folder ID for notes
   */
  private async ensureFolderId(): Promise<string> {
    if (!this.notesFolderId) {
      // Try to get from metadata first
      const stored = await syncMetadataDB.get(`folder_${this.spaceId}_notes`);
      if (stored) {
        this.notesFolderId = stored;
      } else {
        await this.initialize();
      }
    }
    return this.notesFolderId!;
  }

  /**
   * Ensure we have the folder ID for events
   */
  private async ensureEventsFolderId(): Promise<string> {
    if (!this.eventsFolderId) {
      // Try to get from metadata first
      const stored = await syncMetadataDB.get(`folder_${this.spaceId}_events`);
      if (stored) {
        this.eventsFolderId = stored;
      } else {
        await this.initialize();
      }
    }
    return this.eventsFolderId!;
  }

  /**
   * Ensure we have the folder ID for content items
   */
  private async ensureContentItemsFolderId(): Promise<string> {
    if (!this.contentItemsFolderId) {
      // Try to get from metadata first
      const stored = await syncMetadataDB.get(`folder_${this.spaceId}_contentItems`);
      if (stored) {
        this.contentItemsFolderId = stored;
      } else {
        await this.initialize();
      }
    }
    return this.contentItemsFolderId!;
  }

  /**
   * Upload a single note to Google Drive
   */
  async uploadNote(note: Note): Promise<void> {
    // Skip uploading empty "Untitled Note" with no content
    // Handle both plain text and Tiptap JSON content
    const plainText = typeof note.content === 'string'
      ? note.content
      : extractPlainText(note.content as JSONContent);

    if (note.title === 'Untitled Note' && plainText.trim() === '') {
      console.log(`Skipping upload of empty note ${note.id}`);
      // Mark as synced locally so it doesn't keep trying to upload
      await notesDB.updateSyncOnly(note.id, {
        syncStatus: 'synced',
      });
      return;
    }

    try {
      const folderId = await this.ensureFolderId();

      const syncData: NoteSyncData = {
        note,
        version: 1,
      };

      const fileName = `${note.id}.json`;

      // Check if file already exists (using driveFileId if available, otherwise search by name)
      let existingFile;
      if (note.driveFileId) {
        // We know the Drive file ID, use it directly
        try {
          existingFile = { id: note.driveFileId };
        } catch (error) {
          // Drive file ID is invalid, search by name
          console.warn(`Note ${note.id} has invalid driveFileId, searching by name`);
          existingFile = await driveClient.findFile(fileName, folderId);
        }
      } else {
        // No Drive file ID, search by name
        existingFile = await driveClient.findFile(fileName, folderId);
      }

      if (existingFile) {
        // Try to update existing file
        try {
          await driveClient.updateFile(existingFile.id, syncData);

          // CRITICAL: Always ensure driveFileId is stored after update
          await notesDB.updateSyncOnly(note.id, {
            driveFileId: existingFile.id,
            syncStatus: 'synced',
          });
        } catch (updateError: any) {
          // If file was deleted from Drive (404), check if it was intentionally deleted
          const is404 = updateError?.message?.includes('404') ||
                       updateError?.message?.includes('not found') ||
                       updateError?.message?.includes('Not Found');

          if (is404) {
            // Check if this note was intentionally deleted (e.g., from another device)
            const wasDeleted = await deletedItemsDB.isDeleted(note.id);

            if (wasDeleted) {
              // This note was deleted from another device - don't recreate it
              console.log(`[SyncEngine] Note ${note.id} was deleted from Drive by another device, removing locally`);
              await notesDB.delete(note.id);
              return; // Exit without error
            }

            // File was deleted but note wasn't intentionally deleted - recreate it
            console.warn(`[SyncEngine] Drive file ${existingFile.id} not found (404), creating new file`);

            // Upload as new file
            const uploaded = await driveClient.uploadFile(fileName, syncData, folderId);

            // Update note with new Drive file ID
            await notesDB.updateSyncOnly(note.id, {
              driveFileId: uploaded.id,
              syncStatus: 'synced',
            });
          } else {
            // Re-throw non-404 errors
            throw updateError;
          }
        }
      } else {
        // Upload new file
        const uploaded = await driveClient.uploadFile(fileName, syncData, folderId);

        // Update note with Drive file ID
        await notesDB.updateSyncOnly(note.id, {
          driveFileId: uploaded.id,
          syncStatus: 'synced',
        });
      }
    } catch (error) {
      console.error(`Failed to upload note ${note.id}:`, error);

      // Check if this is a 404 error (folder not found) - need to reinitialize
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('404') || errorMessage.includes('not found') || errorMessage.includes('Not Found')) {
        console.warn(`Folder not found, clearing cached folder ID and will reinitialize on next sync`);
        // Clear cached folder ID so it will be re-created on next sync
        this.notesFolderId = null;
        await syncMetadataDB.set(`folder_${this.spaceId}_notes`, '');
      }

      await notesDB.updateSyncOnly(note.id, {
        syncStatus: 'error',
        syncError: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  /**
   * Download a single note from Google Drive
   */
  async downloadNote(fileId: string): Promise<Note | null> {
    try {
      const content = await driveClient.downloadFile(fileId);
      const syncData: NoteSyncData = JSON.parse(content);

      console.log(`[SyncEngine] Processing note "${syncData.note.title}" (${syncData.note.id})`);

      // Check if this note was intentionally deleted locally
      const wasDeleted = await deletedItemsDB.isDeleted(syncData.note.id);
      if (wasDeleted) {
        // This note was deleted locally, so delete it from Drive too
        console.log(`[SyncEngine] Note ${syncData.note.id} was deleted locally, removing from Drive`);
        try {
          await driveClient.deleteFile(fileId);
        } catch (error: any) {
          // Ignore 404 errors - file already deleted
          const is404 = error?.status === 404 || error?.message?.includes('404') || error?.message?.toLowerCase().includes('not found');
          if (!is404) {
            console.error('[SyncEngine] Error deleting file from Drive:', error);
          } else {
            console.log('[SyncEngine] File already deleted from Drive (404), skipping');
          }
        }
        return null;
      }

      // Check if note already exists locally
      const existingNote = await notesDB.get(syncData.note.id);

      if (existingNote) {
        console.log(`[SyncEngine] Note exists locally with status: ${existingNote.syncStatus}`);

        // IMPORTANT: Skip notes with pending status - they have unsaved local changes
        // This prevents overwriting data while user is actively typing
        if (existingNote.syncStatus === 'pending') {
          console.log(`[SyncEngine] Skipping download of note ${syncData.note.id} - has pending local changes`);
          return existingNote;
        }

        // Conflict resolution: newer wins
        const localTime = new Date(existingNote.updatedAt).getTime();
        const remoteTime = new Date(syncData.note.updatedAt).getTime();

        console.log(`[SyncEngine] Conflict resolution: local=${new Date(localTime).toISOString()}, remote=${new Date(remoteTime).toISOString()}`);

        if (remoteTime > localTime) {
          // Remote is newer, update local (preserve the remote's updatedAt)
          console.log(`[SyncEngine] Remote is newer, updating local copy`);
          await notesDB.updateSyncOnly(syncData.note.id, {
            ...syncData.note,
            driveFileId: fileId,
            syncStatus: 'synced',
          });
        } else {
          // Local is newer or same, mark as synced but keep local version (don't touch updatedAt)
          console.log(`[SyncEngine] Local is newer or same, keeping local version`);
          await notesDB.updateSyncOnly(syncData.note.id, {
            syncStatus: 'synced',
            driveFileId: fileId,
          });
        }
      } else {
        // New note from Drive, add to local DB
        console.log(`[SyncEngine] New note from Drive, adding to local DB`);
        await notesDB.create({
          ...syncData.note,
          driveFileId: fileId,
          syncStatus: 'synced',
        });
      }

      return syncData.note;
    } catch (error) {
      console.error(`[SyncEngine] Failed to download note from file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Sync all notes for the current space
   */
  async syncAllNotes(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      uploaded: 0,
      downloaded: 0,
      errors: [],
    };

    try {
      const folderId = await this.ensureFolderId();

      // Get all local notes that need syncing (pending status)
      const localNotes = await notesDB.getBySpace(this.spaceId);
      const pendingNotes = localNotes.filter((note) => note.syncStatus === 'pending');

      // Track which notes we uploaded in this sync cycle
      const uploadedNoteIds = new Set<string>();

      // Upload pending notes
      for (const note of pendingNotes) {
        try {
          await this.uploadNote(note);
          uploadedNoteIds.add(note.id);
          result.uploaded++;
        } catch (error) {
          result.errors.push(`Failed to upload note ${note.title}: ${error}`);
          result.success = false;
        }
      }

      // Get all files from Drive AFTER uploads complete
      const driveFiles = await driveClient.listFiles(folderId, 'application/json');

      // Download notes from Drive that we don't have locally or are newer
      for (const file of driveFiles) {
        try {
          const note = await this.downloadNote(file.id);
          // Only count as downloaded if note wasn't null (null means deleted)
          if (note) {
            result.downloaded++;
          }
        } catch (error) {
          result.errors.push(`Failed to download note ${file.name}: ${error}`);
          result.success = false;
        }
      }

      // CRITICAL: Detect notes that exist locally but have been deleted from Drive
      // This handles the case where a note was deleted from Drive (e.g., via mobile or other device)
      // but still exists in local IndexedDB
      const driveNoteIds = new Set(
        driveFiles.map(file => file.name.replace('.json', ''))
      );

      // Re-fetch local notes after uploads/downloads to get current state
      const currentLocalNotes = await notesDB.getBySpace(this.spaceId);

      // Find local notes that don't have a corresponding file in Drive
      // Only check notes that are marked as 'synced' AND were not just uploaded in this sync cycle
      const syncedNotes = currentLocalNotes.filter(
        note => note.syncStatus === 'synced' && !uploadedNoteIds.has(note.id)
      );

      for (const localNote of syncedNotes) {
        // Skip if this note exists in Drive
        if (driveNoteIds.has(localNote.id)) {
          continue;
        }

        // SAFETY CHECK #1: Only delete if note has been synced before (has driveFileId)
        // This prevents deleting notes that are in the process of being uploaded
        if (!localNote.driveFileId) {
          console.warn(`[SyncEngine] Skipping deletion of note "${localNote.title}" - no driveFileId found`);
          continue;
        }

        // SAFETY CHECK #2: Don't delete notes with error or pending status
        // These might be in the middle of a failed upload that can be retried
        if (localNote.syncStatus === 'error' || localNote.syncStatus === 'pending') {
          console.warn(`[SyncEngine] Skipping deletion of note "${localNote.title}" - sync status is ${localNote.syncStatus}`);
          continue;
        }

        // SAFETY CHECK #3: Don't delete notes created in the last 5 minutes (grace period)
        // This protects against race conditions where upload is still in progress
        const noteAge = Date.now() - new Date(localNote.createdAt).getTime();
        const GRACE_PERIOD = 5 * 60 * 1000; // 5 minutes
        if (noteAge < GRACE_PERIOD) {
          const ageSeconds = Math.floor(noteAge / 1000);
          console.warn(`[SyncEngine] Skipping deletion of note "${localNote.title}" - too recent (created ${ageSeconds}s ago, grace period is 300s)`);
          continue;
        }

        // All safety checks passed - this note was deleted from Drive
        console.log(`[SyncEngine] 🗑️ Detected Drive deletion for note "${localNote.title}" (${localNote.id})`);
        console.log(`[SyncEngine] Note age: ${Math.floor(noteAge / 1000)}s, has driveFileId: ${!!localNote.driveFileId}, status: ${localNote.syncStatus}`);

        try {
          // Delete from local database
          await notesDB.delete(localNote.id);

          // Track deletion to prevent re-download if it somehow appears again
          await deletedItemsDB.add(localNote.id, 'note');

          console.log(`[SyncEngine] ✅ Deleted local note "${localNote.title}" to match Drive state`);
        } catch (error) {
          console.error(`[SyncEngine] ❌ Failed to delete orphaned note ${localNote.id}:`, error);
          result.errors.push(`Failed to delete orphaned note ${localNote.title}: ${error}`);
          result.success = false;
        }
      }

      // Update last sync time
      await syncMetadataDB.set('lastSync', new Date().toISOString());
    } catch (error) {
      console.error('Sync failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Pull all notes from Drive (useful on first login)
   */
  async pullFromDrive(): Promise<Note[]> {
    try {
      console.log(`[SyncEngine] Pulling notes from Drive for space: ${this.spaceId}`);
      const folderId = await this.ensureFolderId();
      console.log(`[SyncEngine] Notes folder ID: ${folderId}`);

      const driveFiles = await driveClient.listFiles(folderId, 'application/json');
      console.log(`[SyncEngine] Found ${driveFiles.length} note files in Drive`);

      const notes: Note[] = [];
      for (const file of driveFiles) {
        try {
          console.log(`[SyncEngine] Downloading note: ${file.name} (${file.id})`);
          const note = await this.downloadNote(file.id);
          if (note) {
            // Only add if not null (null means it was deleted)
            notes.push(note);
            console.log(`[SyncEngine] ✅ Downloaded note: "${note.title}"`);
          } else {
            console.log(`[SyncEngine] ⏭️ Skipped deleted note: ${file.name}`);
          }
        } catch (error) {
          console.error(`[SyncEngine] ❌ Failed to pull note ${file.name}:`, error);
        }
      }

      console.log(`[SyncEngine] Pull complete: ${notes.length} notes downloaded`);
      return notes;
    } catch (error) {
      console.error('[SyncEngine] Failed to pull from Drive:', error);
      throw error;
    }
  }

  /**
   * Delete a note from Drive
   */
  async deleteNoteFromDrive(driveFileId: string): Promise<void> {
    try {
      await driveClient.deleteFile(driveFileId);
    } catch (error: any) {
      // If file not found (404), consider it already deleted - success
      if (error?.status === 404 || error?.message?.includes('404')) {
        console.log(`Note already deleted from Drive (404): ${driveFileId}`);
        return;
      }
      console.error(`Failed to delete note from Drive:`, error);
      throw error;
    }
  }

  /**
   * Upload a single event to Google Drive
   */
  async uploadEvent(event: CalendarEvent): Promise<void> {
    // Skip uploading empty "Untitled Event" with no description
    if (event.title === 'Untitled Event' && (!event.description || event.description.trim() === '')) {
      console.log(`Skipping upload of empty event ${event.id}`);
      // Mark as synced locally so it doesn't keep trying to upload
      await eventsDB.update(event.id, {
        syncStatus: 'synced',
      });
      return;
    }

    try {
      const folderId = await this.ensureEventsFolderId();

      const syncData: EventSyncData = {
        event,
        version: 1,
      };

      const fileName = `${event.id}.json`;

      // Check if file already exists (using driveFileId if available, otherwise search by name)
      let existingFile;
      if (event.driveFileId) {
        // We know the Drive file ID, use it directly
        try {
          existingFile = { id: event.driveFileId };
        } catch (error) {
          // Drive file ID is invalid, search by name
          console.warn(`Event ${event.id} has invalid driveFileId, searching by name`);
          existingFile = await driveClient.findFile(fileName, folderId);
        }
      } else {
        // No Drive file ID, search by name
        existingFile = await driveClient.findFile(fileName, folderId);
      }

      if (existingFile) {
        // Update existing file
        await driveClient.updateFile(existingFile.id, syncData);

        // CRITICAL: Always ensure driveFileId is stored after update
        await eventsDB.update(event.id, {
          driveFileId: existingFile.id,
          syncStatus: 'synced',
        });
      } else {
        // Upload new file
        const uploaded = await driveClient.uploadFile(fileName, syncData, folderId);

        // Update event with Drive file ID
        await eventsDB.update(event.id, {
          driveFileId: uploaded.id,
          syncStatus: 'synced',
        });
      }

      // Also sync to Google Calendar if enabled
      await this.syncToGoogleCalendar(event);
    } catch (error) {
      console.error(`Failed to upload event ${event.id}:`, error);
      await eventsDB.update(event.id, {
        syncStatus: 'error',
        syncError: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  /**
   * Sync a NIDOnote event to Google Calendar (one-way)
   */
  private async syncToGoogleCalendar(event: CalendarEvent): Promise<void> {
    try {
      // Case 1: Sync disabled but event exists in Google Calendar - delete it
      if (!event.syncToGoogleCalendar && event.googleCalendarEventId) {
        console.log(`[Sync] Deleting event from Google Calendar (sync disabled): ${event.googleCalendarEventId}`);
        await calendarClient.deleteEvent(event.googleCalendarEventId);

        // Clear Google Calendar metadata
        await eventsDB.update(event.id, {
          googleCalendarEventId: null,
          googleCalendarSyncStatus: null,
          googleCalendarSyncError: null,
          lastGoogleCalendarSync: null,
        });
        return;
      }

      // Case 2: Sync disabled and no Google Calendar event - nothing to do
      if (!event.syncToGoogleCalendar) {
        return;
      }

      // Case 3: Sync enabled - create or update
      // Convert NIDOnote event to Google Calendar format
      const gcalEvent = calendarClient.createEventFromNIDOnote({
        title: event.title,
        description: event.description,
        startTime: event.startTime,
        endTime: event.endTime,
        isAllDay: event.isAllDay,
        location: event.location,
      });

      if (event.googleCalendarEventId) {
        // Update existing Google Calendar event
        await calendarClient.updateEvent(event.googleCalendarEventId, gcalEvent);

        // Update sync status
        await eventsDB.update(event.id, {
          googleCalendarSyncStatus: 'synced',
          lastGoogleCalendarSync: new Date().toISOString(),
          googleCalendarSyncError: null,
        });
      } else {
        // Create new Google Calendar event
        const gcalId = await calendarClient.createEvent(gcalEvent);

        // Store the Google Calendar event ID
        await eventsDB.update(event.id, {
          googleCalendarEventId: gcalId,
          googleCalendarSyncStatus: 'synced',
          lastGoogleCalendarSync: new Date().toISOString(),
          googleCalendarSyncError: null,
        });
      }
    } catch (error) {
      console.error('Failed to sync to Google Calendar:', error);
      await eventsDB.update(event.id, {
        googleCalendarSyncStatus: 'error',
        googleCalendarSyncError: error instanceof Error ? error.message : 'Sync failed',
      });
      // Don't throw - Google Calendar sync failure shouldn't prevent Drive sync
    }
  }

  /**
   * Download a single event from Google Drive
   */
  async downloadEvent(fileId: string): Promise<CalendarEvent | null> {
    try {
      const content = await driveClient.downloadFile(fileId);
      const syncData: EventSyncData = JSON.parse(content);

      // Check if this event was intentionally deleted locally
      const wasDeleted = await deletedItemsDB.isDeleted(syncData.event.id);
      if (wasDeleted) {
        // This event was deleted locally, so delete it from Drive too
        console.log(`Event ${syncData.event.id} was deleted locally, removing from Drive`);
        try {
          await driveClient.deleteFile(fileId);
        } catch (error: any) {
          // Ignore 404 errors - file already deleted
          const is404 = error?.status === 404 || error?.message?.includes('404') || error?.message?.toLowerCase().includes('not found');
          if (!is404) {
            console.error('Error deleting file from Drive:', error);
          } else {
            console.log('File already deleted from Drive (404), skipping');
          }
        }
        return null;
      }

      // Check if event already exists locally
      const existingEvent = await eventsDB.get(syncData.event.id);

      if (existingEvent) {
        // IMPORTANT: Skip events with pending status - they have unsaved local changes
        if (existingEvent.syncStatus === 'pending') {
          console.log(`Skipping download of event ${syncData.event.id} - has pending local changes`);
          return existingEvent;
        }

        // Conflict resolution: newer wins
        const localTime = new Date(existingEvent.updatedAt).getTime();
        const remoteTime = new Date(syncData.event.updatedAt).getTime();

        if (remoteTime > localTime) {
          // Remote is newer, update local
          await eventsDB.update(syncData.event.id, {
            ...syncData.event,
            driveFileId: fileId,
            syncStatus: 'synced',
          });
        } else {
          // Local is newer or same, mark as synced but keep local version
          await eventsDB.update(syncData.event.id, {
            syncStatus: 'synced',
            driveFileId: fileId,
          });
        }
      } else {
        // New event from Drive, add to local DB with existing ID
        await eventsDB.create({
          ...syncData.event,
          driveFileId: fileId,
          syncStatus: 'synced',
        });
      }

      return syncData.event;
    } catch (error) {
      console.error(`Failed to download event from file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Sync all events for the current space
   */
  async syncAllEvents(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      uploaded: 0,
      downloaded: 0,
      errors: [],
    };

    try {
      const folderId = await this.ensureEventsFolderId();

      // Get all local events that need syncing (pending status)
      const localEvents = await eventsDB.getBySpace(this.spaceId);
      const pendingEvents = localEvents.filter((event) => event.syncStatus === 'pending');

      // Track which events we uploaded in this sync cycle
      const uploadedEventIds = new Set<string>();

      // Upload pending events
      for (const event of pendingEvents) {
        try {
          await this.uploadEvent(event);
          uploadedEventIds.add(event.id);
          result.uploaded++;
        } catch (error) {
          console.error(`[SyncEngine] Failed to upload event "${event.title}":`, error);
          result.errors.push(`Failed to upload event ${event.title}: ${error}`);
          result.success = false;
        }
      }

      // Get all files from Drive AFTER uploads complete
      const driveFiles = await driveClient.listFiles(folderId, 'application/json');

      // Download events from Drive that we don't have locally or are newer
      for (const file of driveFiles) {
        try {
          const event = await this.downloadEvent(file.id);
          // Only count as downloaded if event wasn't null (null means deleted)
          if (event) {
            result.downloaded++;
          }
        } catch (error) {
          console.error(`[SyncEngine] Failed to download event file "${file.name}":`, error);
          result.errors.push(`Failed to download event ${file.name}: ${error}`);
          result.success = false;
        }
      }

      // CRITICAL: Detect events that exist locally but have been deleted from Drive
      // This handles the case where an event was deleted from Drive (e.g., via mobile or other device)
      // but still exists in local IndexedDB
      const driveEventIds = new Set(
        driveFiles.map(file => file.name.replace('.json', ''))
      );

      // Re-fetch local events after uploads/downloads to get current state
      const currentLocalEvents = await eventsDB.getBySpace(this.spaceId);

      // Find local events that don't have a corresponding file in Drive
      // Only check events that are marked as 'synced' AND were not just uploaded in this sync cycle
      const syncedEvents = currentLocalEvents.filter(
        event => event.syncStatus === 'synced' && !uploadedEventIds.has(event.id)
      );

      for (const localEvent of syncedEvents) {
        // Skip if this event exists in Drive
        if (driveEventIds.has(localEvent.id)) {
          continue;
        }

        // SAFETY CHECK #1: Only delete if event has been synced before (has driveFileId)
        // This prevents deleting events that are in the process of being uploaded
        if (!localEvent.driveFileId) {
          console.warn(`[SyncEngine] Skipping deletion of event "${localEvent.title}" - no driveFileId found`);
          continue;
        }

        // SAFETY CHECK #2: Don't delete events with error or pending status
        // These might be in the middle of a failed upload that can be retried
        if (localEvent.syncStatus === 'error' || localEvent.syncStatus === 'pending') {
          console.warn(`[SyncEngine] Skipping deletion of event "${localEvent.title}" - sync status is ${localEvent.syncStatus}`);
          continue;
        }

        // SAFETY CHECK #3: Don't delete events created in the last 5 minutes (grace period)
        // This protects against race conditions where upload is still in progress
        const eventAge = Date.now() - new Date(localEvent.createdAt).getTime();
        const GRACE_PERIOD = 5 * 60 * 1000; // 5 minutes
        if (eventAge < GRACE_PERIOD) {
          const ageSeconds = Math.floor(eventAge / 1000);
          console.warn(`[SyncEngine] Skipping deletion of event "${localEvent.title}" - too recent (created ${ageSeconds}s ago, grace period is 300s)`);
          continue;
        }

        // All safety checks passed - this event was deleted from Drive
        console.log(`[SyncEngine] 🗑️ Detected Drive deletion for event "${localEvent.title}" (${localEvent.id})`);
        console.log(`[SyncEngine] Event age: ${Math.floor(eventAge / 1000)}s, has driveFileId: ${!!localEvent.driveFileId}, status: ${localEvent.syncStatus}`);

        try {
          // Delete from local database
          await eventsDB.delete(localEvent.id);

          // Track deletion to prevent re-download if it somehow appears again
          await deletedItemsDB.add(localEvent.id, 'event');

          console.log(`[SyncEngine] ✅ Deleted local event "${localEvent.title}" to match Drive state`);
        } catch (error) {
          console.error(`[SyncEngine] ❌ Failed to delete orphaned event ${localEvent.id}:`, error);
          result.errors.push(`Failed to delete orphaned event ${localEvent.title}: ${error}`);
          result.success = false;
        }
      }

      // Update last sync time
      await syncMetadataDB.set('lastEventsSync', new Date().toISOString());
    } catch (error) {
      console.error('[SyncEngine] Events sync failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Pull all events from Drive (useful on first login)
   */
  async pullEventsFromDrive(): Promise<CalendarEvent[]> {
    try {
      const folderId = await this.ensureEventsFolderId();
      const driveFiles = await driveClient.listFiles(folderId, 'application/json');

      const events: CalendarEvent[] = [];
      for (const file of driveFiles) {
        try {
          const event = await this.downloadEvent(file.id);
          if (event) {
            // Only add if not null (null means it was deleted)
            events.push(event);
          }
        } catch (error) {
          console.error(`Failed to pull event ${file.name}:`, error);
        }
      }

      return events;
    } catch (error) {
      console.error('Failed to pull events from Drive:', error);
      throw error;
    }
  }

  /**
   * Delete an event from Drive and Google Calendar
   */
  async deleteEventFromDrive(driveFileId: string, googleCalendarEventId?: string | null): Promise<void> {
    try {
      await driveClient.deleteFile(driveFileId);
    } catch (error: any) {
      // If file not found (404), consider it already deleted - success
      if (error?.status === 404 || error?.message?.includes('404')) {
        console.log(`Event already deleted from Drive (404): ${driveFileId}`);
        return;
      }
      console.error(`Failed to delete event from Drive:`, error);
      throw error;
    }

    // Also delete from Google Calendar if it exists
    if (googleCalendarEventId) {
      try {
        await calendarClient.deleteEvent(googleCalendarEventId);
      } catch (error) {
        console.error('Failed to delete from Google Calendar:', error);
        // Continue - don't fail the entire operation if Google Calendar delete fails
      }
    }
  }

  /**
   * Upload a single content item to Google Drive
   */
  async uploadContentItem(item: ContentItem): Promise<void> {
    // Skip uploading empty "Untitled" items with no content
    if (item.title === 'Untitled' && (!item.content || item.content.trim() === '')) {
      console.log(`Skipping upload of empty content item ${item.id}`);
      // Mark as synced locally so it doesn't keep trying to upload
      await contentItemsDB.update(item.id, {
        syncStatus: 'synced',
      });
      return;
    }

    try {
      const folderId = await this.ensureContentItemsFolderId();

      const syncData: ContentItemSyncData = {
        item,
        version: 1,
      };

      const fileName = `${item.id}.json`;

      // Check if file already exists (using driveFileId if available, otherwise search by name)
      let existingFile;
      if (item.driveFileId) {
        // We know the Drive file ID, use it directly
        try {
          existingFile = { id: item.driveFileId };
        } catch (error) {
          // Drive file ID is invalid, search by name
          console.warn(`ContentItem ${item.id} has invalid driveFileId, searching by name`);
          existingFile = await driveClient.findFile(fileName, folderId);
        }
      } else {
        // No Drive file ID, search by name
        existingFile = await driveClient.findFile(fileName, folderId);
      }

      if (existingFile) {
        // Update existing file
        await driveClient.updateFile(existingFile.id, syncData);

        // CRITICAL: Always ensure driveFileId is stored after update
        await contentItemsDB.update(item.id, {
          driveFileId: existingFile.id,
          syncStatus: 'synced',
        });
      } else {
        // Upload new file
        const uploaded = await driveClient.uploadFile(fileName, syncData, folderId);

        // Update content item with Drive file ID
        await contentItemsDB.update(item.id, {
          driveFileId: uploaded.id,
          syncStatus: 'synced',
        });
      }
    } catch (error) {
      console.error(`Failed to upload content item ${item.id}:`, error);
      await contentItemsDB.update(item.id, {
        syncStatus: 'error',
        syncError: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  /**
   * Download a single content item from Google Drive
   */
  async downloadContentItem(fileId: string): Promise<ContentItem | null> {
    try {
      const content = await driveClient.downloadFile(fileId);
      const syncData: ContentItemSyncData = JSON.parse(content);

      // Check if this item was intentionally deleted locally
      const wasDeleted = await deletedItemsDB.isDeleted(syncData.item.id);
      if (wasDeleted) {
        // This item was deleted locally, so delete it from Drive too
        console.log(`ContentItem ${syncData.item.id} was deleted locally, removing from Drive`);
        try {
          await driveClient.deleteFile(fileId);
        } catch (error: any) {
          // Ignore 404 errors - file already deleted
          const is404 = error?.status === 404 || error?.message?.includes('404') || error?.message?.toLowerCase().includes('not found');
          if (!is404) {
            console.error('Error deleting file from Drive:', error);
          } else {
            console.log('File already deleted from Drive (404), skipping');
          }
        }
        return null;
      }

      // Check if content item already exists locally
      const existingItem = await contentItemsDB.get(syncData.item.id);

      if (existingItem) {
        // IMPORTANT: Skip items with pending status - they have unsaved local changes
        // This prevents overwriting data while user is actively typing
        if (existingItem.syncStatus === 'pending') {
          console.log(`Skipping download of content item ${syncData.item.id} - has pending local changes`);
          return existingItem;
        }

        // Conflict resolution: newer wins
        const localTime = new Date(existingItem.updatedAt).getTime();
        const remoteTime = new Date(syncData.item.updatedAt).getTime();

        if (remoteTime > localTime) {
          // Remote is newer, update local (preserve the remote's updatedAt)
          await contentItemsDB.update(syncData.item.id, {
            ...syncData.item,
            driveFileId: fileId,
            syncStatus: 'synced',
          });
        } else {
          // Local is newer or same, mark as synced but keep local version (don't touch updatedAt)
          await contentItemsDB.update(syncData.item.id, {
            syncStatus: 'synced',
            driveFileId: fileId,
          });
        }
      } else {
        // New content item from Drive, add to local DB
        await contentItemsDB.create({
          ...syncData.item,
          driveFileId: fileId,
          syncStatus: 'synced',
        });
      }

      return syncData.item;
    } catch (error) {
      console.error(`Failed to download content item from file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Sync all content items for the current space
   */
  async syncAllContentItems(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      uploaded: 0,
      downloaded: 0,
      errors: [],
    };

    try {
      const folderId = await this.ensureContentItemsFolderId();

      // Get all local content items that need syncing (pending status)
      const localItems = await contentItemsDB.getBySpace(this.spaceId);
      const pendingItems = localItems.filter((item) => item.syncStatus === 'pending');

      // Track which items we uploaded in this sync cycle
      const uploadedItemIds = new Set<string>();

      // Upload pending content items
      for (const item of pendingItems) {
        try {
          await this.uploadContentItem(item);
          uploadedItemIds.add(item.id);
          result.uploaded++;
        } catch (error) {
          console.error(`[SyncEngine] Failed to upload content item "${item.title}":`, error);
          result.errors.push(`Failed to upload content item ${item.title}: ${error}`);
          result.success = false;
        }
      }

      // Get all files from Drive AFTER uploads complete
      const driveFiles = await driveClient.listFiles(folderId, 'application/json');

      // Download content items from Drive that we don't have locally or are newer
      for (const file of driveFiles) {
        try {
          const item = await this.downloadContentItem(file.id);
          // Only count as downloaded if item wasn't null (null means deleted)
          if (item) {
            result.downloaded++;
          }
        } catch (error) {
          console.error(`[SyncEngine] Failed to download content item file "${file.name}":`, error);
          result.errors.push(`Failed to download content item ${file.name}: ${error}`);
          result.success = false;
        }
      }

      // CRITICAL: Detect content items that exist locally but have been deleted from Drive
      // This handles the case where an item was deleted from Drive (e.g., via mobile or other device)
      // but still exists in local IndexedDB
      const driveItemIds = new Set(
        driveFiles.map(file => file.name.replace('.json', ''))
      );

      // Re-fetch local content items after uploads/downloads to get current state
      const currentLocalItems = await contentItemsDB.getBySpace(this.spaceId);

      // Find local content items that don't have a corresponding file in Drive
      // Only check items that are marked as 'synced' AND were not just uploaded in this sync cycle
      const syncedItems = currentLocalItems.filter(
        item => item.syncStatus === 'synced' && !uploadedItemIds.has(item.id)
      );

      for (const localItem of syncedItems) {
        // Skip if this item exists in Drive
        if (driveItemIds.has(localItem.id)) {
          continue;
        }

        // SAFETY CHECK: Only delete if item has been synced before (has driveFileId)
        // This prevents deleting items that are in the process of being uploaded
        if (!localItem.driveFileId) {
          console.warn(`[SyncEngine] Skipping deletion of content item "${localItem.title}" - no driveFileId found`);
          continue;
        }

        // This item exists locally but not in Drive - it was deleted from Drive
        console.log(`[SyncEngine] Detected Drive deletion for content item "${localItem.title}" (${localItem.id})`);

        try {
          // Delete from local database
          await contentItemsDB.delete(localItem.id);

          // Track deletion to prevent re-download if it somehow appears again
          await deletedItemsDB.add(localItem.id, 'contentItem');

          console.log(`[SyncEngine] Deleted local content item "${localItem.title}" to match Drive state`);
        } catch (error) {
          console.error(`[SyncEngine] Failed to delete orphaned content item ${localItem.id}:`, error);
          result.errors.push(`Failed to delete orphaned content item ${localItem.title}: ${error}`);
          result.success = false;
        }
      }

      // Update last sync time
      await syncMetadataDB.set('lastContentItemsSync', new Date().toISOString());
    } catch (error) {
      console.error('[SyncEngine] Content items sync failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Pull all content items from Drive (useful on first login)
   */
  async pullContentItemsFromDrive(): Promise<ContentItem[]> {
    try {
      const folderId = await this.ensureContentItemsFolderId();
      const driveFiles = await driveClient.listFiles(folderId, 'application/json');

      const items: ContentItem[] = [];
      for (const file of driveFiles) {
        try {
          const item = await this.downloadContentItem(file.id);
          if (item) {
            // Only add if not null (null means it was deleted)
            items.push(item);
          }
        } catch (error) {
          console.error(`Failed to pull content item ${file.name}:`, error);
        }
      }

      return items;
    } catch (error) {
      console.error('Failed to pull content items from Drive:', error);
      throw error;
    }
  }

  /**
   * Delete a content item from Drive
   */
  async deleteContentItemFromDrive(driveFileId: string): Promise<void> {
    try {
      await driveClient.deleteFile(driveFileId);
    } catch (error: any) {
      // If file not found (404), consider it already deleted - success
      if (error?.status === 404 || error?.message?.includes('404')) {
        console.log(`ContentItem already deleted from Drive (404): ${driveFileId}`);
        return;
      }
      console.error(`Failed to delete content item from Drive:`, error);
      throw error;
    }
  }
}

// Export singleton for default space
export const defaultSyncEngine = new SyncEngine('default');

/**
 * Helper function to get sync engine for a space
 */
export function getSyncEngine(spaceId: string): SyncEngine {
  return new SyncEngine(spaceId);
}
